import React, { useState } from "react";
import { Link } from "react-router-dom";
import BirthInput from "./BirthInput";
import { Birth, runCalculator } from "../../utils/calculatorApi";
import "./VedicCalculator.css";
import "./DailyHoroscope.css";

/* Shape of the horoscope_app payload. categories is an OBJECT keyed by
   career / wealth / love / health, not an array, which is easy to get wrong. */
interface Category {
  score: number;
  sentiment: string;
  best_time: string;
  predictions: string[];
  action_point: string[];
}

interface Overview {
  insights: string[];
  action_point: string[];
  good_time: string;
  bad_time: string;
  lucky_number: number | string;
  lucky_color: string;
  primary_theme: string;
  day_rating: string;
  day_score: number;
  suggested_questions?: string[];
}

interface Highlight {
  key: string;
  severity: string;
  text: string;
}

interface HoroscopeApp {
  horoscope: {
    overview: Overview;
    categories: Record<string, Category>;
    transits?: unknown[];
    /* An object with the mahadasha, antardasha and pratyantardasha lines
       plus a combined summary. Not an array. */
    dasha_insights?: {
      md?: string;
      ad?: string;
      pd?: string;
      combined?: string;
    };
  };
  highlights?: Highlight[];
  date?: string;
}

/* The API writes em and en dashes throughout. Neither belongs in our copy, so
   every string is normalised before it reaches the page. A dash sitting
   between two clock times is a range and reads as "to"; every other dash is a
   clause break and reads as a comma. Doubled or orphaned commas from that
   substitution are then cleaned up. */
export const deDash = (text: string) =>
  text
    .replace(/\s*[–—]\s*(?=\d{1,2}[:.]\d{2})/g, " to ")
    .replace(/\s*[–—]\s*/g, ", ")
    .replace(/,(\s*,)+/g, ",")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/,(\s*[.;:])/g, "$1");

/* The API sends markdown bold. The bold half is the claim, the rest is the
   reason and the advice, so it has to render as emphasis rather than leak
   asterisks into the page. Only ** is supported, which is all the API uses. */
const Rich: React.FC<{ text: string }> = ({ text }) => (
  <>
    {deDash(text).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    )}
  </>
);

/* day_rating arrives as a word plus stars, e.g. "Favorable ⭐⭐⭐⭐". Colour is
   driven by the word, never by the score, so the label and the colour can
   never disagree. */
const RATING_TONE: Record<string, string> = {
  Exceptional: "tone-exceptional",
  Favorable: "tone-favorable",
  Moderate: "tone-moderate",
  Challenging: "tone-challenging",
  Difficult: "tone-difficult",
};

/* The five sentiments the API guarantees. Kept as the i18n keys the app
   already uses, so translations stay shared rather than invented here. */
const SENTIMENT_TONE: Record<string, string> = {
  Excellent: "tone-exceptional",
  Favorable: "tone-favorable",
  Dynamic: "tone-dynamic",
  Moderate: "tone-moderate",
  Challenging: "tone-challenging",
};

const CATEGORY_LABEL: Record<string, string> = {
  career: "Career",
  wealth: "Money",
  love: "Love",
  health: "Health",
};

const SEVERITY_TONE: Record<string, string> = {
  favourable: "tone-favorable",
  favorable: "tone-favorable",
  moderate: "tone-moderate",
  caution: "tone-challenging",
  high: "tone-difficult",
};

/* Scores are 30-92 by design: no honest daily reading is 0 or 100. The bar is
   still scaled 0-100 so the number on it means what it says, it simply never
   reaches either end. */
const Bar: React.FC<{ score: number }> = ({ score }) => (
  <div className="dh-bar" role="presentation">
    <span style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
  </div>
);

const Skeleton: React.FC = () => (
  <div className="vedcalc-result dh-skeleton" role="status" aria-live="polite">
    <span className="dh-sr">Reading your chart…</span>
    <div className="dh-sk dh-sk-title" />
    <div className="dh-sk dh-sk-line" />
    <div className="dh-sk dh-sk-line short" />
    <div className="dh-sk-grid">
      {[0, 1, 2, 3].map((i) => (
        <div className="dh-sk dh-sk-card" key={i} />
      ))}
    </div>
  </div>
);

interface DailyHoroscopeProps {
  /**
   * Optional gate, run before the reading is fetched. Return true to allow it,
   * false to stop so the caller can open the auth modal instead.
   *
   * Left undefined on /daily-horoscope, which is a public SEO page and has to
   * stay usable logged out. Supplied on /horoscope, a campaign page whose job
   * is the signup. BirthInput has already validated the details by this point,
   * so nobody is asked to sign in and then told their input was wrong.
   */
  authGate?: () => boolean;
}

const DailyHoroscope: React.FC<DailyHoroscopeProps> = ({ authGate }) => {
  const [data, setData] = useState<HoroscopeApp | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");

  const run = async (birth: Birth) => {
    if (authGate && !authGate()) return;
    setBusy(true);
    setFailed("");
    setData(null);
    try {
      /* Cached for a day, keyed on the birth details, because the server-side
         cache does not reliably hit and every miss is a full computation. */
      setData(await runCalculator<HoroscopeApp>("horoscope_app", birth));
    } catch (e) {
      setFailed(
        e instanceof Error ? e.message : "Could not read your horoscope."
      );
    } finally {
      setBusy(false);
    }
  };

  const ov = data?.horoscope?.overview;
  const cats = data?.horoscope?.categories;
  const ratingWord = ov?.day_rating?.trim().split(/\s+/)[0] ?? "";
  const ratingStars = ov?.day_rating?.trim().split(/\s+/).slice(1).join(" ") ?? "";

  return (
    <div className="vedcalc">
      <BirthInput onSubmit={run} busy={busy} cta="Read my day" />

      {busy ? <Skeleton /> : null}

      {failed ? (
        <div className="vedcalc-result vedcalc-failed" role="status">
          <p>{failed}</p>
        </div>
      ) : null}

      {ov && cats ? (
        <div className="vedcalc-result dh" role="status">
          <div className="dh-head">
            <div>
              <p className="dh-theme">{deDash(ov.primary_theme)}</p>
              <p className={`dh-rating ${RATING_TONE[ratingWord] || ""}`}>
                {ratingWord} <span className="dh-stars">{ratingStars}</span>
              </p>
            </div>
            <div className="dh-score">
              <span className="dh-score-num">{ov.day_score}</span>
              <Bar score={ov.day_score} />
            </div>
          </div>

          {/* Real panchang for the birth place today: Abhijit muhurat and Rahu
              Kaal. Hidden entirely when the panchang call failed and the API
              returned "" rather than a fabricated time. */}
          {ov.good_time || ov.bad_time ? (
            <div className="dh-times">
              {ov.good_time ? (
                <span className="dh-pill dh-good">
                  <b>Auspicious</b> {deDash(ov.good_time)}
                </span>
              ) : null}
              {ov.bad_time ? (
                <span className="dh-pill dh-bad">
                  <b>Avoid</b> {deDash(ov.bad_time)}
                </span>
              ) : null}
            </div>
          ) : null}

          {ov.insights?.length ? (
            <ul className="dh-insights">
              {ov.insights.map((t, i) => (
                <li key={i}>
                  <Rich text={t} />
                </li>
              ))}
            </ul>
          ) : null}

          {ov.action_point?.length ? (
            <div className="dh-action">
              <span className="dh-action-label">Do this today</span>
              {ov.action_point.map((t, i) => (
                <p key={i}>
                  <Rich text={t} />
                </p>
              ))}
            </div>
          ) : null}

          <div className="dh-cats">
            {Object.entries(cats).map(([key, c]) => (
              <div className="dh-cat" key={key}>
                <div className="dh-cat-top">
                  <h3>{CATEGORY_LABEL[key] || key}</h3>
                  <span
                    className={`dh-chip ${SENTIMENT_TONE[c.sentiment] || ""}`}
                  >
                    {c.sentiment}
                  </span>
                </div>
                <div className="dh-cat-score">
                  <span>{c.score}</span>
                  <Bar score={c.score} />
                </div>
                {c.predictions?.length ? (
                  <ul className="dh-preds">
                    {c.predictions.map((p, i) => (
                      <li key={i}>
                        <Rich text={p} />
                      </li>
                    ))}
                  </ul>
                ) : null}
                {c.action_point?.length
                  ? c.action_point.map((a, i) => (
                      <p className="dh-cat-action" key={i}>
                        <Rich text={a} />
                      </p>
                    ))
                  : null}
                {/* A real planetary hour from today's sunrise and sunset. */}
                {c.best_time ? (
                  <p className="dh-best">Best window {deDash(c.best_time)}</p>
                ) : null}
              </div>
            ))}
          </div>

          {/* Shown alongside the day rating on purpose. A strong day inside a
              hard multi-year period is what the classical system describes, so
              these must not be averaged into one number. */}
          {data?.highlights?.length ? (
            <div className="dh-highlights">
              {data.highlights.map((h, i) => (
                <p
                  key={i}
                  className={`dh-highlight ${SEVERITY_TONE[h.severity] || ""}`}
                >
                  {deDash(h.text)}
                </p>
              ))}
            </div>
          ) : null}

          {(() => {
            const d = data?.horoscope?.dasha_insights;
            if (!d) return null;
            const rows: [string, string | undefined][] = [
              ["Mahadasha", d.md],
              ["Antardasha", d.ad],
              ["Pratyantardasha", d.pd],
            ];
            const shown = rows.filter(([, v]) => v);
            if (!shown.length && !d.combined) return null;
            return (
              <div className="dh-dasha">
                <h3>Your running periods</h3>
                {shown.map(([label, v]) => (
                  <p key={label}>
                    <span className="dh-dasha-label">{label}</span>
                    <Rich text={v as string} />
                  </p>
                ))}
                {d.combined ? (
                  <p className="dh-dasha-combined">
                    <Rich text={d.combined} />
                  </p>
                ) : null}
              </div>
            );
          })()}

          <div className="dh-lucky">
            {ov.lucky_number ? (
              <span className="dh-pill">
                <b>Lucky number</b> {ov.lucky_number}
              </span>
            ) : null}
            {ov.lucky_color ? (
              <span className="dh-pill">
                <b>Lucky colour</b> {deDash(ov.lucky_color)}
              </span>
            ) : null}
          </div>

          <p className="vedcalc-note">
            This reading is computed from your birth chart and today's
            planetary positions, not written by a language model. The auspicious
            and inauspicious windows are the real panchang for your birth place.
            Scores sit between 30 and 92 because no honest daily reading is
            either nothing or everything.
          </p>

          <Link to="/chat" className="vedcalc-cta">
            Ask Aura AI about your day
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default DailyHoroscope;
